import { LinearProgress } from '@material-ui/core'
import { FC, Fragment } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import {
  createApartment,
  removeApartment,
  updateApartmentCode
} from '../../actions/apartments'
import { navigate } from '../../actions/routes'
import {
  CreateApartment,
  Navigate,
  RemoveApartment,
  UpdateApartmentCode
} from '../../actions/types'
import Container from '../../components/Container'
import {
  selectApartmentsAsArray,
  selectIsApartmentsLoading
} from '../../selectors/apartments'
import {
  selectIsApplicationsLoading,
  selectApplications
} from '../../selectors/applications'
import { selectApartmentStatusFromPathname } from '../../selectors/router'
import {
  selectShowingsInterfaces,
  selectShowingsLoading
} from '../../selectors/showings'
import { selectIsAdminUser, selectIsMobile } from '../../selectors/users'
import { Apartment } from '../../types/apartments'
import {
  IndexedApplications,
  StateInterface,
  IndexedShowingsInterfaces
} from '../../types/states'
import { compareCreatedAtDates } from '../../utils/sorting'
import NavApartmentStatuses from '../Navigation/Menus/NavApartmentStatuses'
import AdminApartments from './AdminView/AdminApartments'
import UserApartments from './UserView/UserApartments'

export interface ApartmentsShowingProps {
  areShowingsLoading: boolean
  showingsInterfaces: IndexedShowingsInterfaces
}

interface PropsFromState {
  status: string
  isAdmin: boolean
  isApartmentsLoading: boolean
  isApplicationsLoading: boolean
  apartments: Apartment[]
  applications: IndexedApplications
  areShowingsLoading: boolean
  showingsInterfaces: IndexedShowingsInterfaces
  isMobile: boolean
}

const mapStateToProps = (state: StateInterface): PropsFromState => {
  const status = selectApartmentStatusFromPathname(state)
  return {
    status,
    isAdmin: selectIsAdminUser(state),
    isApartmentsLoading: selectIsApartmentsLoading(state),
    isApplicationsLoading: selectIsApplicationsLoading(state),
    apartments:
      selectApartmentsAsArray(state).sort(compareCreatedAtDates) || [],
    applications: selectApplications(state),
    areShowingsLoading: selectShowingsLoading(state),
    showingsInterfaces: selectShowingsInterfaces(state),
    isMobile: selectIsMobile(state) || false
  }
}

interface DispatchProps {
  createApartment: CreateApartment
  removeApartment: RemoveApartment
  updateApartmentCode: UpdateApartmentCode
  navigateToPath: Navigate
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  navigateToPath: (path: string) => dispatch(navigate(path)),
  createApartment: () => dispatch(createApartment()),
  removeApartment: (apartmentId: string) =>
    dispatch(removeApartment(apartmentId)),
  updateApartmentCode: (apartmentId: string) =>
    dispatch(updateApartmentCode(apartmentId))
})

type ApartmentsProps = DispatchProps & PropsFromState & WithTranslation

const Apartments: FC<ApartmentsProps> = ({
  t,
  createApartment,
  removeApartment,
  updateApartmentCode,
  navigateToPath,
  status,
  apartments,
  applications,
  isAdmin,
  isApartmentsLoading,
  isApplicationsLoading,
  isMobile,
  areShowingsLoading,
  showingsInterfaces
}: ApartmentsProps) => {

  return (
    <Fragment>
      {isApartmentsLoading ? (
        <LinearProgress />
      ) : (
        <Container>
          {isAdmin ? (
            <Fragment>
              <NavApartmentStatuses t={t} />
              <AdminApartments
                navigate={navigateToPath}
                removeApartment={removeApartment}
                updateApartmentCode={updateApartmentCode}
                apartments={apartments}
                applications={applications}
                t={t}
                showingsProps={{ areShowingsLoading, showingsInterfaces }}
              />
            </Fragment>
          ) : (
            <UserApartments
              isMobile={isMobile}
              navigate={navigateToPath}
              removeApartment={removeApartment}
              createApartment={createApartment}
              apartments={apartments}
              t={t}
            />
          )}
        </Container>
      )}
    </Fragment>
  )
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Apartments))
